import { useEffect, useRef, useState } from 'react';
import { Address } from 'viem';

import { useGetUnstakePeriodQuery } from 'modules/api/actions/getUnstakePeriod';
import { ONE_MINUTE_MS } from 'modules/common/const';
import { useTimeLeftText } from 'modules/common/hooks/useTimeLeftText';
import { getClaimTimeLeft } from 'modules/pool/utils/getClaimTimeLeft';

interface IUseIsClaimableResult {
  isClaimable: boolean;
  timeLeft: string | null;
}

export function useIsClaimable(
  executedAt: Date,
  poolAddress: Address,
): IUseIsClaimableResult {
  const [timeLeft, setTimeLeft] = useState<Date | null>(null);
  const [isClaimable, setIsClaimable] = useState(false);
  const isMountedRef = useRef(true);

  const { data: unstakePeriodDays } = useGetUnstakePeriodQuery({
    poolAddress,
  });

  useEffect(() => {
    isMountedRef.current = true;

    const updateTimer = () => {
      if (!unstakePeriodDays || !isMountedRef.current) {
        return;
      }

      const timeLeft = getClaimTimeLeft(executedAt, unstakePeriodDays);

      if (timeLeft === null) {
        if (isMountedRef.current) {
          setTimeLeft(null);
          setIsClaimable(true);
        }
      } else if (isMountedRef.current) {
        setTimeLeft(timeLeft);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, ONE_MINUTE_MS);

    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
    };
  }, [unstakePeriodDays, executedAt]);

  const timeLeftText = useTimeLeftText(timeLeft);

  return { isClaimable, timeLeft: timeLeftText };
}
